import { useEffect, useCallback } from "react";
import { useAuth, useUser } from "@clerk/react";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppSidebar } from "@/components/dashboard/AppSidebar";
import { PromptInput } from "@/components/dashboard/PromptInput";
import { TemplatesGallery } from "@/components/dashboard/TemplatesGallery";
import { DashboardTour } from "@/components/dashboard/DashboardTour";
import { LowCreditBanner } from "@/components/billing/LowCreditBanner";
import { CheckoutSuccessModal } from "@/components/billing/CheckoutSuccessModal";
import { useTourStore } from "@/lib/stores/tour-store";
import { createOnboardingApi } from "@/lib/api/onboarding";

// Dark mode: deep, saturated aurora
const auroraGradientDark = [
  "radial-gradient(ellipse at 50% 0%, oklch(0.15 0.05 250) 0%, transparent 50%)",
  "radial-gradient(ellipse at 30% 40%, oklch(0.35 0.15 285) 0%, transparent 40%)",
  "radial-gradient(ellipse at 70% 50%, oklch(0.45 0.2 350) 0%, transparent 40%)",
  "linear-gradient(to top, oklch(0.5 0.25 30) 0%, transparent 50%)",
  // "linear-gradient(to top, oklch(0.9 0.1 30) 10%, transparent 40%)",
].join(", ");

// Light mode: soft, pastel aurora
const auroraGradientLight = [
  "radial-gradient(ellipse at 50% 0%, oklch(0.85 0.04 250) 0%, transparent 50%)",
  "radial-gradient(ellipse at 30% 40%, oklch(0.82 0.08 285) 0%, transparent 40%)",
  "radial-gradient(ellipse at 70% 50%, oklch(0.88 0.1 350) 0%, transparent 40%)",
  "radial-gradient(ellipse at 50% 70%, oklch(0.9 0.1 30) 0%, transparent 40%)",
  "linear-gradient(to top, oklch(0.9 0.1 30) 10%, transparent 40%)",
].join(", ");

export function DashboardPage() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const startTour = useTourStore((s) => s.start);

  useEffect(() => {
    const flag = sessionStorage.getItem("rayloop_start_tour");
    if (!flag) return;
    sessionStorage.removeItem("rayloop_start_tour");
    if (user?.publicMetadata?.tourComplete === true) return;
    startTour();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTourComplete = useCallback(
    (skipped: boolean) => {
      const api = createOnboardingApi({ getToken });
      api.completeTour(skipped).then(() => user?.reload()).catch(() => {});
    },
    [getToken, user],
  );

  return (
    <TooltipProvider>
      <CheckoutSuccessModal />
      <SidebarProvider className="h-svh min-h-0! overflow-hidden">
        <AppSidebar />
        <SidebarInset className="max-h-svh overflow-hidden bg-white dark:bg-[#1c1c1c]">
          {/* Aurora gradient — light mode (soft pastels) */}
          <div
            className="pointer-events-none absolute inset-0 z-0 dark:hidden"
            style={{ background: auroraGradientLight }}
          />
          {/* Aurora gradient — dark mode (deep saturated) */}
          <div
            className="pointer-events-none absolute inset-0 z-0 hidden dark:block"
            style={{ background: auroraGradientDark }}
          />

          {/* Hamburger menu for mobile */}
          <div className="sticky top-0 z-20 flex items-center gap-2 px-4 py-3 md:hidden">
            <SidebarTrigger />
          </div>

          {/* Low credit banner */}
          <div className="relative z-10 px-4 pt-2 md:px-8">
            <LowCreditBanner />
          </div>

          {/* Scrollable content layer */}
          <div className="relative z-10 flex h-svh flex-col overflow-y-auto px-4 md:px-8">
            {/* Prompt area — reduced height so templates peek */}
            <div className="flex min-h-[calc(100svh-6rem)] flex-col items-center justify-center">
              <PromptInput />
            </div>

            {/* Templates gallery — scrolls into view */}
            <TemplatesGallery />
          </div>
        </SidebarInset>

        <DashboardTour onComplete={handleTourComplete} />
      </SidebarProvider>
    </TooltipProvider>
  );
}
